import * as mobx from 'mobx';
import classNames from 'classnames';
import * as IoC from 'typescript-ioc';
import React, { useState } from 'react';
import './audio-bridge-connections.scss';
import Text from '../../../../global/text/text';
import { Component } from '../../../../../infrastructure/type-extensions';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { CommonPopover } from '../../../../global/common-popover/common-popover';
import { addText, cancelText } from '../../../../../constants/common-labels.constants';
import { AudioBridgeProviderLabel } from '../../../../../infrastructure/utils/common.utils';
import { OptionIcon, OptionIconChecked } from '../../../../global/oe-radio-options/option-icon';
import { CommonPopoverContext } from '../../../../global/common-popover/common-popover.context';
import { alertServerError, showNotification } from '../../../../global/modal/alerts-host/alert-templates';
import { DefaultClosePopoverButton } from '../../../../global/common-popover/default-close-popover-button';
import { AudioBridgeProvider, ConnectionType, IConnectionOption } from '../../../../../api/connections.dao';
import { createUniqueDailInConnection, getConnectionItemLabelText } from '../../../../../infrastructure/utils/connections.utils';
import { AUDIO_BRIDGE_CONNECTION_LIST_TEXT, UNIQUE_DIALIN_CONNECTION_TOAST } from '../../../../../constants/audio-bridge-connection.constant';
import { UniqueDialInData } from '../../../../../api/zoom.dao';
import DefaultButton from '../../../../global/default-button/default-button';

interface IFormContainerDerivedProps {
    isOpen: boolean;
}

const makeAssignToConnectionsInPopover = <T extends IFormContainerDerivedProps>(
    PopoverTriggerComponent: Component<T>
) => {
    const ComponentWithForwardedRef = React.forwardRef<HTMLElement, T>((props: T, ref: React.Ref<HTMLElement>): JSX.Element => {
        return (
            <CommonPopoverContext.Consumer>
                {context => <PopoverTriggerComponent {...props} isOpen={context.isOpen} ref={ref} />}
            </CommonPopoverContext.Consumer>
        );
    });

    return props => {
        const {
            onChange,
            meetingConnections,
            setModelOpen,
            setStatusModalOpen,
            attendeeInfo,
        } = props;

        const [isSaving, setIsSaving] = useState<boolean>(false);
        const [selectedConnection, setSelectedConnection] = useState<{ id: number | null; audioBridgeID: string | null }>({ id: null, audioBridgeID: null });

        const optionsMap = meetingConnections
            .filter(item => !item.cancelled)
            .reduce((_map, item) => {
                const isTurboBridgeConnection = item.type === ConnectionType.AudioBridge
                    && item.provider === AudioBridgeProviderLabel[AudioBridgeProvider.TURBOBRIDGE];
                if (isTurboBridgeConnection) {
                    _map[item.meetingConnectionId] = {
                        id: item.meetingConnectionId,
                        value: getConnectionItemLabelText(item),
                        audioBridgeID: item.audioBridgeID
                    };
                }
                return _map;
            }, {}) as Record<number, IConnectionOption>;

        const optionsList: IConnectionOption[] = [...Object.values(optionsMap)];
        const selectedAudioBridgeId = optionsList.find(option => option.id === props.assignedConnectionId)?.audioBridgeID || null;

        const handleOnOptionSelect = (newOptionId: number, connectionOption?: string): void => {
            onChange(newOptionId, connectionOption);
            setModelOpen(false);
        };
        const handleCancelOption = (): void => {
            setSelectedConnection({
                id: props.assignedConnectionId || null,
                audioBridgeID: selectedAudioBridgeId
            });
            setModelOpen(false);
        };
        const handleAddAudioBridgeConnection = async (): Promise<UniqueDialInData> => {
            setIsSaving(true);
            try {
                const response = await createUniqueDailInConnection(selectedConnection.audioBridgeID, selectedConnection.id, attendeeInfo);
                if (response) {
                    handleOnOptionSelect(selectedConnection.id);
                    setModelOpen(false);
                    setStatusModalOpen(false);
                    showNotification(UNIQUE_DIALIN_CONNECTION_TOAST);
                }
                return response;
            } catch (error) {
                const errorParsingService = IoC.Container.get(IErrorParsing);
                const errorMessage = errorParsingService.extractMessage(error);
                alertServerError('Error', errorMessage);
                return;
            } finally {
                mobx.runInAction(() => {
                    setIsSaving(false);
                });
            }
        };

        return (
            <CommonPopover
                className='oe-popup-layout__body--fixed-width'
                header={
                    <span className='oe-popup-layout__header-title'>
                        {AUDIO_BRIDGE_CONNECTION_LIST_TEXT}
                    </span>}
                body={(
                    <div className="audiobridge-connection-dropdown__options">
                        {optionsList.map(option => (
                            <div
                                key={option.id}
                                className='audiobridge-connection-dropdown__oe-radio-option'
                                style={{ width: 'auto' }}
                                onClick={() => setSelectedConnection({ id: option.id, audioBridgeID: option.audioBridgeID })}
                            >
                                <input
                                    type='radio'
                                    id={`radio-button-${option.id}${option.value}`}
                                    className='oe-radio-option__control'
                                    checked={option.id === selectedConnection.id}
                                />
                                <label htmlFor={`radio-button-${option.id}${option.value}`}
                                    className={classNames('oe-radio-option__label', {
                                        'oe-radio-option--selected': option.id === selectedConnection.id
                                    })}
                                    onClick={e => {
                                        e.stopPropagation();
                                        setSelectedConnection({ id: option.id, audioBridgeID: option.audioBridgeID });
                                    }}>
                                    {option.id === selectedConnection.id ? <OptionIconChecked /> : <OptionIcon />}
                                    <Text
                                        className='oe-radio-option__label-text'
                                        id={option.id?.toString()}
                                        message={option.value}
                                        withEllipsis
                                    />
                                </label>
                            </div>
                        ))}
                    </div>
                )}
                footer={
                    <>
                        <div className='buttons-block buttons-block--from-right'>
                            <DefaultButton
                                onClick={() => {
                                    if (selectedConnection.id) {
                                        handleAddAudioBridgeConnection();
                                    }
                                }}
                                modifier="confirm-safe"
                                disabled={isSaving}
                                text={addText()}
                            />
                            <DefaultClosePopoverButton
                                className='default-button--cancel'
                                onClick={() => handleCancelOption()}
                                text={cancelText()}
                            />
                        </div>
                    </>
                }
                position='left'
                align='start'
                onClose={handleCancelOption}
            >
                <ComponentWithForwardedRef {...props} />
            </CommonPopover>
        );
    };
};


export { makeAssignToConnectionsInPopover, IFormContainerDerivedProps };